import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Copy, QrCode, Loader2, ArrowRightLeft, Wallet, Users } from "lucide-react";

// Tokens by chain (matching signup.ts wallets)
const TOKENS_BY_CHAIN: Record<string, string[]> = {
  solana: ["SOL", "USDT", "FIXERCOIN", "PINGX", "LOCKER", "FXM", "USDC", "BONK"],
  bitcoin: ["BTC"],
  ethereum: ["ETH", "USDT", "USDC", "DAI", "UNI", "LINK", "SHIBA INU"],
  bsc: ["BNB", "USDT", "USDC", "BUSD", "CAKE"],
  polygon: ["MATIC", "USDT", "USDC", "DAI"],
  tron: ["TRX", "USDT"],
};

// Chain display names
const CHAIN_NAMES: Record<string, string> = {
  solana: "SOLANA",
  bitcoin: "BITCOIN",
  ethereum: "ETHEREUM",
  bsc: "BSC",
  polygon: "POLYGON",
  tron: "TRON",
};

type DepositMethod = "exchange" | "wallet" | "p2p" | null;

// ---------- METHOD SELECTION DIALOG ----------
const MethodSelectDialog = ({ show, onSelect }: { show: boolean; onSelect: (m: DepositMethod) => void }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-6 shadow-2xl space-y-5">
        <div className="text-center">
          <h2 className="text-sm font-bold uppercase tracking-wider text-yellow-400">
            SELECT DEPOSIT METHOD
          </h2>
          <p className="text-[10px] text-muted-foreground uppercase mt-2">
            CHOOSE HOW YOU WANT TO ADD FUNDS
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => onSelect("exchange")}
            className="w-full flex items-center gap-3 p-4 rounded-lg border border-border bg-background hover:border-yellow-400/60 hover:bg-yellow-400/5 transition text-left"
          >
            <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
              <ArrowRightLeft className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-bold uppercase text-foreground">FROM FIXORIUM EXCHANGE</p>
              <p className="text-[9px] text-muted-foreground uppercase mt-0.5">CONVERT OR SWAP TOKENS</p>
            </div>
          </button>

          <button
            onClick={() => onSelect("wallet")}
            className="w-full flex items-center gap-3 p-4 rounded-lg border border-border bg-background hover:border-yellow-400/60 hover:bg-yellow-400/5 transition text-left"
          >
            <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-bold uppercase text-foreground">WALLET ADDRESS</p>
              <p className="text-[9px] text-muted-foreground uppercase mt-0.5">DEPOSIT FROM EXTERNAL WALLET</p>
            </div>
          </button>

          <button
            onClick={() => onSelect("p2p")}
            className="w-full flex items-center gap-3 p-4 rounded-lg border border-border bg-background hover:border-yellow-400/60 hover:bg-yellow-400/5 transition text-left"
          >
            <div className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-bold uppercase text-foreground">P2P TRADE</p>
              <p className="text-[9px] text-muted-foreground uppercase mt-0.5">BUY FROM OTHER USERS</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// Round/circular QR display (used for both desktop and mobile)
const RoundQRCodeDisplay = ({ address, chain, size = "md" }: { address: string | null; chain: string; size?: "sm" | "md" }) => {
  const qrUrl = address
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(address)}`
    : null;

  const dims = size === "sm" ? "w-14 h-14 p-1 border-2" : "w-40 h-40 p-2 border-4";
  const iconDims = size === "sm" ? "w-6 h-6" : "w-12 h-12";

  if (!address) {
    return (
      <div className={`${dims} rounded-full border-yellow-400/40 bg-muted/30 flex items-center justify-center`}>
        <QrCode className={iconDims + " text-muted-foreground"} />
      </div>
    );
  }

  return (
    <div className={`${dims} rounded-full border-yellow-400/60 bg-white shadow-lg shadow-yellow-400/20 overflow-hidden flex items-center justify-center flex-shrink-0`}>
      <img
        src={qrUrl!}
        alt={`${CHAIN_NAMES[chain]} Wallet QR Code`}
        className="w-full h-full object-contain rounded-full"
      />
    </div>
  );
};

export default function Deposit() {
  const [user, setUser] = useState<any | null>(null);
  const [token, setToken] = useState("SOL");
  const [chainId, setChainId] = useState("solana");
  const [addressForChain, setAddressForChain] = useState<string | null>(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // Method-selection state
  const [showMethodDialog, setShowMethodDialog] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState<DepositMethod>(null);

  // QR visibility state (hidden until user clicks)
  const [showQR, setShowQR] = useState(false);

  const navigate = useNavigate();

  // Detect screen size for responsive layout
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============ SECURE USER LOADING (using HttpOnly cookie) ============
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
            const chainMap: Record<string, string> = {
              solana: data.user.wallets?.solana,
              bitcoin: data.user.wallets?.bitcoin,
              ethereum: data.user.wallets?.ethereum,
              bsc: data.user.wallets?.bsc,
              polygon: data.user.wallets?.polygon,
              tron: data.user.wallets?.tron,
            };
            setAddressForChain(chainMap[chainId] || null);
            setLoading(false);
            return;
          }
        }
        navigate("/login");
      } catch (error) {
        console.error("[Deposit] Auth error:", error);
        navigate("/login");
      }
    };
    fetchUser();
  }, [navigate, chainId]);

  // Handle network change
  const handleNetworkChange = (newChain: string) => {
    setChainId(newChain);
    const tokens = TOKENS_BY_CHAIN[newChain];
    if (tokens && tokens.length > 0) setToken(tokens[0]);
    if (user?.wallets) {
      const chainMap: Record<string, string> = {
        solana: user.wallets.solana,
        bitcoin: user.wallets.bitcoin,
        ethereum: user.wallets.ethereum,
        bsc: user.wallets.bsc,
        polygon: user.wallets.polygon,
        tron: user.wallets.tron,
      };
      setAddressForChain(chainMap[newChain] || null);
    }
  };

  // Load wallet from backend if address missing (fallback)
  useEffect(() => {
    if (!user?.email) return;
    if (addressForChain) return;
    
    const loadWalletFromAPI = async () => {
      setAddressLoading(true);
      try {
        const response = await fetch(`/api/wallet?email=${encodeURIComponent(user.email)}`, {
          credentials: "include",
        });
        if (!response.ok) throw new Error("Failed to fetch wallet");
        const data = await response.json();
        const wallets = data?.wallets || data;
        const chainMap: Record<string, string> = {
          solana: wallets?.solana,
          bitcoin: wallets?.bitcoin,
          ethereum: wallets?.ethereum,
          bsc: wallets?.bsc,
          polygon: wallets?.polygon,
          tron: wallets?.tron,
        };
        const chainAddress = chainMap[chainId] || null;
        setAddressForChain(chainAddress);
        setUser((prev: any) => ({
          ...prev,
          wallets: { ...(prev?.wallets || {}), ...chainMap },
        }));
      } catch (e) {
        console.error("[Deposit] Wallet fetch error:", e);
      } finally {
        setAddressLoading(false);
      }
    };
    loadWalletFromAPI();
  }, [user?.email, chainId, addressForChain]);

  // Handle method choice from dialog
  const handleMethodSelect = (method: DepositMethod) => {
    setSelectedMethod(method);
    setShowMethodDialog(false);
    setShowQR(false);
    if (method === "exchange") {
      navigate("/transfer");
    } else if (method === "p2p") {
      navigate("/p2p");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-400 mx-auto mb-4" />
          <p className="text-[10px] uppercase text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Method selection dialog (shows on open) */}
      <MethodSelectDialog show={showMethodDialog} onSelect={handleMethodSelect} />

      {isDesktop ? (
        // ============ DESKTOP VIEW — single centered column, header left + QR right ============
        <div className="p-6 min-h-screen flex justify-center">
          <div className="w-full max-w-2xl bg-card rounded-xl p-6 border border-border">
            {/* Header row: DEPOSIT (left) + QR button/image (right) */}
            <div className="flex items-start justify-between gap-4 mb-6 pb-4 border-b border-border">
              <div>
                <h1 className="text-2xl font-bold text-foreground uppercase">DEPOSIT</h1>
                <p className="text-xs text-muted-foreground uppercase mt-1">SEND FUNDS TO YOUR WALLET</p>
              </div>

              <div className="flex-shrink-0">
                {!showQR ? (
                  <Button
                    onClick={() => setShowQR(true)}
                    disabled={!addressForChain || addressLoading}
                    className="bg-yellow-400 text-black hover:bg-yellow-500 text-[10px] uppercase font-bold h-10 px-4"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    SHOW QR CODE
                  </Button>
                ) : (
                  <button
                    onClick={() => setShowQR(false)}
                    className="cursor-pointer"
                    title="Click to hide QR code"
                  >
                    <RoundQRCodeDisplay address={addressForChain} chain={chainId} size="md" />
                  </button>
                )}
              </div>
            </div>

            {/* Form */}
            <div className="space-y-6">
              {/* Network */}
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">NETWORK</label>
                <select
                  value={chainId}
                  onChange={(e) => handleNetworkChange(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-[10px] uppercase focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="solana">SOLANA</option>
                  <option value="bitcoin">BITCOIN</option>
                  <option value="ethereum">ETHEREUM</option>
                  <option value="bsc">BSC</option>
                  <option value="polygon">POLYGON</option>
                  <option value="tron">TRON</option>
                </select>
              </div>

              {/* Token */}
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">TOKEN</label>
                <select
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-[10px] uppercase focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  {TOKENS_BY_CHAIN[chainId]?.map((t) => (
                    <option key={t} className="bg-background text-foreground">
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {/* Address */}
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">ADDRESS DETECTED</label>
                <div className="mt-2 flex gap-2">
                  <Input
                    readOnly
                    value={
                      addressLoading
                        ? "Loading..."
                        : addressForChain || `No ${CHAIN_NAMES[chainId]} address available`
                    }
                    className="text-[10px] uppercase bg-background border-border font-mono text-foreground"
                    disabled={addressLoading}
                  />
                  <Button
                    size="sm"
                    disabled={!addressForChain || addressLoading}
                    onClick={() => {
                      if (addressForChain) {
                        navigator.clipboard?.writeText(addressForChain);
                        toast({
                          title: "ADDRESS COPIED",
                          description: `${CHAIN_NAMES[chainId]} wallet address copied to clipboard`,
                        });
                      }
                    }}
                    className="text-[10px] uppercase bg-yellow-400 text-black hover:bg-yellow-500"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    COPY
                  </Button>
                </div>
                {!addressForChain && !addressLoading && (
                  <p className="text-[8px] text-yellow-500 mt-2 uppercase">
                    No {CHAIN_NAMES[chainId]} address found. Please contact support.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // ============ MOBILE VIEW — header row: DEPOSIT left, QR button/image right ============
        <section className="w-full min-h-screen bg-background text-foreground px-[9px]">
          <div className="w-full py-6">
            {/* Header row: title on left, QR button/image on right */}
            <div className="flex items-start justify-between gap-3 mb-8">
              <div className="flex items-center gap-3">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">DEPOSIT</h1>
                </div>
              </div>

              <div className="flex-shrink-0">
                {!showQR ? (
                  <Button
                    onClick={() => setShowQR(true)}
                    disabled={!addressForChain || addressLoading}
                    className="bg-yellow-400 text-black hover:bg-yellow-500 text-[10px] uppercase font-bold h-9 px-3"
                  >
                    <QrCode className="w-4 h-4" />
                  </Button>
                ) : (
                  <button
                    onClick={() => setShowQR(false)}
                    className="cursor-pointer"
                    title="Click to hide QR code"
                  >
                    <RoundQRCodeDisplay address={addressForChain} chain={chainId} size="sm" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {/* Network */}
              <div>
                <label className="text-xs font-semibold text-foreground">NETWORK</label>
                <select
                  value={chainId}
                  onChange={(e) => handleNetworkChange(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="solana">SOLANA</option>
                  <option value="bitcoin">BITCOIN</option>
                  <option value="ethereum">ETHEREUM</option>
                  <option value="bsc">BSC</option>
                  <option value="polygon">POLYGON</option>
                  <option value="tron">TRON</option>
                </select>
              </div>

              {/* Token */}
              <div>
                <label className="text-xs font-semibold text-foreground">TOKEN</label>
                <select
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  {TOKENS_BY_CHAIN[chainId]?.map((t) => (
                    <option key={t} className="bg-background text-foreground">
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {/* Address */}
              <div>
                <label className="text-xs font-semibold text-foreground">ADDRESS</label>
                <div className="mt-2 flex gap-2">
                  <Input
                    readOnly
                    value={
                      addressLoading
                        ? "Loading..."
                        : addressForChain || `No ${CHAIN_NAMES[chainId]} address available`
                    }
                    className="text-xs font-mono bg-background border-border text-foreground"
                    disabled={addressLoading}
                  />
                  <Button
                    size="sm"
                    disabled={!addressForChain || addressLoading}
                    onClick={() => {
                      if (addressForChain) {
                        navigator.clipboard?.writeText(addressForChain);
                        toast({
                          title: "ADDRESS COPIED",
                          description: `${CHAIN_NAMES[chainId]} wallet address copied to clipboard`,
                        });
                      }
                    }}
                    className="bg-yellow-400 text-black hover:bg-yellow-500"
                  >
                    COPY
                  </Button>
                </div>
                {!addressForChain && !addressLoading && (
                  <p className="text-xs text-yellow-500 mt-2">
                    No {CHAIN_NAMES[chainId]} address found. Please contact support.
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
